import { FormEvent, useEffect, useState } from "react";
import { toast, ToastContainer } from 'react-toastify';

import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo-2.png";
import { makePostRequest } from "../../request";
import { 
  ChangePasswordFormData, 
  ChangePasswordErrorData, 
  ChangePasswordRequest,
} from "../../interfaces/changePassword.types";

const customStyle: React.CSSProperties = {
  textAlign: "left",
  width: "260px",
  margin: "48px auto",
  marginTop: "48px"
};

const ChangePassword = () => {
  const params = useLocation();
  const email = params.search.substring(7);
  const [formData, setFormData] = useState<ChangePasswordFormData>({
    email: email,
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  
  const [errorData, setErrorData] = useState<ChangePasswordErrorData>({});
  const [iconChange, setIconChange] = useState(false);
  const [iconChange2, setIconChange2] = useState(false);
  const [iconChange3, setIconChange3] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const togglePassword = (inputId: string) => {
    const userPassInput = document.getElementById(inputId) as HTMLInputElement;
    
    if (userPassInput.type === "password") {
      userPassInput.type = "text";
      switch(inputId) {
        case "password4":
          setIconChange(true);
          break;
        case "password5":
          setIconChange2(true);
          break;
        case "password6":
          setIconChange3(true);
          break;
      }
    } else {
      userPassInput.type = "password";
      switch(inputId) {
        case "password4":
          setIconChange(false);
          break;
        case "password5":
          setIconChange2(false);
          break;
        case "password6":
          setIconChange3(false);
          break;
      }
    }
  };

  const isValidPassword = (password: string): boolean => {
    // Define a regular expression pattern for the password conditions
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()-_=+{};:,<.>/?]).{8,}$/;
    return passwordPattern.test(password);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {        
    const { name, value } = event.target;
    setFormData(prevState => ({ 
      ...prevState, 
      [name]: value 
    }));

    // Clear error when user types
    if (errorData[name as keyof ChangePasswordErrorData]) {
      setErrorData(prevState => ({ ...prevState, [name]: '' }));
    }
  };

  const checkPassword = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const newErrors = { ...errorData };
    
    if (value.length >= 8) {
      if (!isValidPassword(value)) {
        newErrors[name as keyof ChangePasswordErrorData] = 'Password must include uppercase, lowercase, number, and special character';
      } else {
        delete newErrors[name as keyof ChangePasswordErrorData];
      }

      // Check if passwords match
      if (name === 'newPassword' || name === 'confirmPassword') {
        if (formData.newPassword !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
      }
      
      setErrorData(newErrors);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    
    // Final validation before submission
    const newErrors: ChangePasswordErrorData = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!isValidPassword(formData.newPassword)) {
      newErrors.newPassword = 'Password must meet all requirements';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrorData(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      const request: ChangePasswordRequest = {
        what: "ThirdPartyChangeUserCreds",
        data: formData
      };
      
      setIsLoading(true);
      
      makePostRequest(request)
        .then((res: {msg: string}) => {  
          setIsLoading(false);
          toast.success(`${res.msg} You'll be redirected shortly to login with the new password.`);
          setTimeout(() => {
            navigate("/login");
          }, 1500);
        })
        .catch((error) => {
          setIsLoading(false);
          toast.error(error.response?.data?.msg || "An error occurred while changing your password.");
        });
    }
  };

  useEffect(() => {
    if (!email) {
      navigate("/");
    }
  }, [email, navigate]);

  return (
    <div className="authentication-container">
      <div className="authentication-modal">
        <div className="inner">
          <img width="50px" src={logo} alt="" />
          <h3>Transit Tracker System</h3>      
          
          <form onSubmit={handleSubmit}> 
            <div style={customStyle}>
              <p className="header-text">Change Password</p>
              <p className="small-text">Replace the system generated password</p>
              <p className='alert-warning'>
                The password must have a minimum of eight characters, including uppercase, lowercase, special characters, and a number.
              </p>
              
              <div className="password-container mt-1">                            
                <input 
                  id="password4" 
                  type="password" 
                  placeholder="Current Password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  onBlur={checkPassword}
                  minLength={8}
                  required
                />
                {iconChange ? (
                  <span onClick={() => togglePassword("password4")} className="material-symbols-rounded">
                    visibility
                  </span>
                ) : (
                  <span onClick={() => togglePassword("password4")} className="material-symbols-rounded">
                    visibility_off
                  </span>
                )}
              </div>     
              <p className="error">{errorData.currentPassword}</p>  
              
              <div className="password-container mt-1">                            
                <input 
                  id="password5" 
                  type="password" 
                  placeholder="New Password" 
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  onBlur={checkPassword}
                  minLength={8}
                  required
                />
                {iconChange2 ? (
                  <span onClick={() => togglePassword("password5")} className="material-symbols-rounded">
                    visibility
                  </span>
                ) : (
                  <span onClick={() => togglePassword("password5")} className="material-symbols-rounded">
                    visibility_off
                  </span>
                )}
              </div>
              <p className="error">{errorData.newPassword}</p>    
              
              <div className="password-container mt-1">                            
                <input 
                  id="password6" 
                  type="password" 
                  placeholder="Confirm Password" 
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={checkPassword}
                  minLength={8}
                  required
                />
                {iconChange3 ? (
                  <span onClick={() => togglePassword("password6")} className="material-symbols-rounded">
                    visibility
                  </span>
                ) : (
                  <span onClick={() => togglePassword("password6")} className="material-symbols-rounded">
                    visibility_off
                  </span>
                )}
              </div> 
              <p className="error">{errorData.confirmPassword}</p>             
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? "Please wait..." : "Change Password"}
            </button>
          </form>

          <Link to="/login" className="link-text">Back to Login</Link>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ChangePassword;